import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from '../layouts/Dashboard/Layout';
import { getTvListCall, storeTvCall, updateTvCall, deleteTvCall, uploadTvLogoCall } from "../../api/Tv";
import LivePlayer from "../common/LivePlayer";

const getPosterUrl = (posterPath) => {
    if (!posterPath) return "https://public.nanbateam.com/logos/appletv.png";
    if (posterPath.startsWith("http://") || posterPath.startsWith("https://") || posterPath.startsWith("data:image/")) {
        return posterPath;
    }
    return `https://public.nanbateam.com/${posterPath.replace(/^\/+/, '').replace(/^images\//, '')}`;
};

const emptyForm = {
    name: "",
    code: "",
    logo: "/images/logos/appletv.png",
    live: "",
    coverage: "",
    website: "",
    email: "",
    mobile: "",
    disclaimer: "",
    status: 1
};

const Tv = () => {
    const navigate = useNavigate();
    const userRole = localStorage.getItem('user_role');
    const isClient = userRole === 'client' || Boolean(localStorage.getItem('client_tv_id'));

    useEffect(() => {
        if (isClient) {
            navigate('/station');
        }
    }, [isClient, navigate]);

    const [tvs, setTvs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters and view mode
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [viewMode, setViewMode] = useState("table"); // "table" or "grid"

    // Multi-selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const headerCheckboxRef = useRef(null);

    // Modals
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
    const [previewStream, setPreviewStream] = useState(null);
    const [currentChannel, setCurrentChannel] = useState(null);

    // Form
    const [formData, setFormData] = useState(emptyForm);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [toastMessage, setToastMessage] = useState(null);

    // Logo state
    const [logoMode, setLogoMode] = useState("url"); // "url" or "upload"
    const [logoFile, setLogoFile] = useState(null);
    const [logoFilePreview, setLogoFilePreview] = useState("");

    const fetchData = (isBackground = false) => {
        if (!isBackground) {
            setLoading(true);
            setError(null);
        }
        getTvListCall()
            .then(res => {
                const list = res?.data?.data?.data || res?.data?.data || [];
                setTvs(Array.isArray(list) ? list : []);
            })
            .catch(err => {
                console.error("Failed to load TV channels:", err);
                if (!isBackground) {
                    setError(err.response?.data?.message || err.message || "Failed to load TV channels");
                    setTvs([]);
                }
            })
            .finally(() => {
                if (!isBackground) {
                    setLoading(false);
                }
            });
    };

    useEffect(() => {
        fetchData(false);
        const timer = setInterval(() => {
            fetchData(true);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3500);
    };

    // Filtered list
    const filteredTvs = useMemo(() => {
        let result = tvs;

        if (statusFilter === "active") {
            result = result.filter(c => c.status === 1 || c.status === undefined || c.status === null);
        } else if (statusFilter === "disabled") {
            result = result.filter(c => c.status === 0);
        }

        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            result = result.filter(c =>
                (c.name && c.name.toLowerCase().includes(q)) ||
                (c.code && c.code.toLowerCase().includes(q)) ||
                (c.coverage && c.coverage.toLowerCase().includes(q)) ||
                (c.live && c.live.toLowerCase().includes(q))
            );
        }

        return result;
    }, [tvs, searchTerm, statusFilter]);

    // Selected channels objects
    const selectedChannels = useMemo(() => {
        return tvs.filter(tv => selectedIds.includes(tv.id));
    }, [tvs, selectedIds]);

    // Select all check state
    const isAllSelected = filteredTvs.length > 0 && filteredTvs.every(tv => selectedIds.includes(tv.id));
    const isSomeSelected = filteredTvs.some(tv => selectedIds.includes(tv.id)) && !isAllSelected;

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = isSomeSelected;
        }
    }, [isSomeSelected]);

    // Selection handlers
    const handleToggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const visibleIds = filteredTvs.map(tv => tv.id);
            setSelectedIds(Array.from(new Set([...selectedIds, ...visibleIds])));
        } else {
            const visibleIds = new Set(filteredTvs.map(tv => tv.id));
            setSelectedIds(prev => prev.filter(id => !visibleIds.has(id)));
        }
    };

    const handleClearSelection = () => {
        setSelectedIds([]);
    };

    // Logo file select handler
    const handleLogoFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/") && !file.name.endsWith(".svg")) {
            alert("Please select a valid image file (PNG, SVG, JPG, WebP, GIF).");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert("Logo file is too large. Maximum allowed size is 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setLogoFile(file);
            setLogoFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Modal Triggers
    const handleOpenAdd = () => {
        setFormData(emptyForm);
        setLogoMode("url");
        setLogoFile(null);
        setLogoFilePreview("");
        setFormError(null);
        setIsAddModalOpen(true);
    };

    const handleOpenEdit = (tv) => {
        setCurrentChannel(tv);
        setFormData({
            id: tv.id,
            name: tv.name || "",
            code: tv.code || "",
            logo: tv.logo || "/images/logos/appletv.png",
            live: tv.live || "",
            coverage: tv.coverage || "",
            website: tv.website || "",
            email: tv.email || "",
            mobile: tv.mobile || "",
            disclaimer: tv.disclaimer || "",
            status: tv.status !== undefined ? tv.status : 1
        });
        setLogoMode("url");
        setLogoFile(null);
        setLogoFilePreview("");
        setFormError(null);
        setIsEditModalOpen(true);
    };

    const handleOpenDelete = (tv) => {
        setCurrentChannel(tv);
        setIsDeleteModalOpen(true);
    };

    const handleOpenBulkDelete = () => {
        if (selectedIds.length === 0) return;
        setIsBulkDeleteModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === "status" ? parseInt(value, 10) : value
        }));
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.code.trim() || !formData.live.trim()) {
            setFormError("Channel Name, Station Code, and Live Stream URL are required.");
            return;
        }

        setFormSubmitting(true);
        setFormError(null);

        let formToSave = { ...formData };
        if (logoMode === "upload" && logoFilePreview) {
            try {
                const uploadRes = await uploadTvLogoCall({
                    tv_id: formData.code || 1,
                    image: logoFilePreview,
                    filename: logoFile?.name || "logo.png"
                });
                if (uploadRes.data?.data?.url) {
                    formToSave.logo = uploadRes.data.data.url;
                }
            } catch (err) {
                formToSave.logo = logoFilePreview;
            }
        }

        storeTvCall(formToSave)
            .then(() => {
                setIsAddModalOpen(false);
                setFormData(emptyForm);
                setLogoFile(null);
                setLogoFilePreview("");
                showToast("Channel created successfully!");
                fetchData();
            })
            .catch(err => {
                setFormError(err.response?.data?.message || err.message || "Failed to create channel");
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.code.trim()) {
            setFormError("Channel Name and Station Code are required.");
            return;
        }

        setFormSubmitting(true);
        setFormError(null);

        let formToSave = { ...formData };
        if (logoMode === "upload" && logoFilePreview) {
            try {
                const uploadRes = await uploadTvLogoCall({
                    tv_id: formData.id || currentChannel?.id,
                    image: logoFilePreview,
                    filename: logoFile?.name || "logo.png"
                });
                if (uploadRes.data?.data?.url) {
                    formToSave.logo = uploadRes.data.data.url;
                }
            } catch (err) {
                formToSave.logo = logoFilePreview;
            }
        }

        updateTvCall(formToSave)
            .then(() => {
                setIsEditModalOpen(false);
                setCurrentChannel(null);
                setLogoFile(null);
                setLogoFilePreview("");
                showToast("Channel updated successfully!");
                fetchData();
            })
            .catch(err => {
                setFormError(err.response?.data?.message || err.message || "Failed to update channel");
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const handleDeleteSubmit = () => {
        if (!currentChannel) return;
        setFormSubmitting(true);

        deleteTvCall({ id: currentChannel.id })
            .then(() => {
                setIsDeleteModalOpen(false);
                setSelectedIds(prev => prev.filter(id => id !== currentChannel.id));
                setCurrentChannel(null);
                showToast("Channel deleted successfully!");
                fetchData();
            })
            .catch(err => {
                alert(err.response?.data?.message || err.message || "Failed to delete channel");
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    // Bulk Delete Submit
    const handleBulkDeleteSubmit = () => {
        if (selectedIds.length === 0) return;
        setFormSubmitting(true);

        deleteTvCall({ ids: selectedIds })
            .then((res) => {
                setIsBulkDeleteModalOpen(false);
                const count = selectedIds.length;
                setSelectedIds([]);
                showToast(`Successfully deleted ${count} channels!`);
                fetchData();
            })
            .catch(err => {
                alert(err.response?.data?.message || err.message || "Failed to delete selected channels");
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text);
        showToast(`${label} copied!`);
    };

    return (
        <Layout>
            {/* Header */}
            <div className="page-header">
                <div className="page-title-group">
                    <h1>TV Channels Directory</h1>
                    <p>Live streaming feeds, RTMP ingest endpoints, coverage regions, and station metadata</p>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                    <button className="btn btn-secondary btn-sm" onClick={fetchData} disabled={loading}>
                        Refresh
                    </button>
                    <button className="btn btn-primary" onClick={handleOpenAdd}>
                        + Add Channel
                    </button>
                </div>
            </div>

            {error && (
                <div style={{
                    padding: "14px 18px",
                    backgroundColor: "var(--accent-rose-light)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "10px",
                    color: "var(--accent-rose)",
                    marginBottom: "20px",
                    fontSize: "13.5px"
                }}>
                    {error}
                </div>
            )}

            {/* Bulk Action Bar (appears when 1 or more channels selected) */}
            {selectedIds.length > 0 && (
                <div className="bulk-action-bar">
                    <div className="bulk-action-info">
                        <span className="bulk-count-badge">{selectedIds.length}</span>
                        <span>station{selectedIds.length > 1 ? "s" : ""} selected for batch operation</span>
                    </div>
                    <div className="bulk-action-buttons">
                        <button className="btn btn-secondary btn-sm" onClick={handleClearSelection}>
                            Clear Selection
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={handleOpenBulkDelete}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                            Delete Selected ({selectedIds.length})
                        </button>
                    </div>
                </div>
            )}

            {/* Actions & Filters Bar */}
            <div className="actions-bar">
                <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                    <div className="search-field">
                        <svg className="search-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            type="text"
                            placeholder="Filter by name, code, coverage, or URL..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <select
                        className="form-control"
                        style={{ width: "150px", height: "40px" }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active Only</option>
                        <option value="disabled">Disabled Only</option>
                    </select>

                    <span style={{ fontSize: "13px", color: "var(--text-muted)", marginLeft: "4px" }}>
                        Showing <strong>{filteredTvs.length}</strong> of {tvs.length} channels
                    </span>
                </div>

                {/* View Switcher */}
                <div className="view-toggle">
                    <button
                        className={`view-toggle-btn ${viewMode === "table" ? "active" : ""}`}
                        onClick={() => setViewMode("table")}
                        title="Table List View"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                        </svg>
                        <span>Table</span>
                    </button>
                    <button
                        className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                        onClick={() => setViewMode("grid")}
                        title="Studio Cards View"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7"></rect>
                            <rect x="14" y="3" width="7" height="7"></rect>
                            <rect x="14" y="14" width="7" height="7"></rect>
                            <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                        <span>Grid</span>
                    </button>
                </div>
            </div>

            {/* VIEW MODE: TABLE */}
            {viewMode === "table" ? (
                <div className="table-container">
                    <table className="studio-table">
                        <thead>
                            <tr>
                                <th style={{ width: "36px", textAlign: "center" }}>
                                    <input
                                        type="checkbox"
                                        className="ui-checkbox"
                                        ref={headerCheckboxRef}
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        title={isAllSelected ? "Deselect All Visible" : "Select All Visible"}
                                    />
                                </th>
                                <th style={{ width: "36px" }}>#</th>
                                <th>Station Name</th>
                                <th>Station Code</th>
                                <th>Coverage</th>
                                <th>Live Watchers</th>
                                <th>RTMP / HLS Stream URL</th>
                                <th>Status</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                                        Loading channels...
                                    </td>
                                </tr>
                            ) : filteredTvs.length > 0 ? (
                                filteredTvs.map((tv, idx) => {
                                    const isSelected = selectedIds.includes(tv.id);
                                    return (
                                        <tr key={tv.id || idx} className={isSelected ? "row-selected" : ""}>
                                            <td style={{ textAlign: "center" }}>
                                                <input
                                                    type="checkbox"
                                                    className="ui-checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleToggleSelect(tv.id)}
                                                    title={`Select ${tv.name}`}
                                                />
                                            </td>
                                            <td style={{ color: "var(--text-muted)", fontSize: "12px" }}>{idx + 1}</td>
                                            <td>
                                                <div className="channel-title-cell">
                                                    <div className="channel-avatar" style={{ overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0f1d", border: "1px solid var(--border-subtle)", padding: "2px" }}>
                                                        {tv.logo ? (
                                                            <img
                                                                src={getPosterUrl(tv.logo)}
                                                                alt={tv.name}
                                                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                onError={(e) => { e.target.style.display = "none"; }}
                                                            />
                                                        ) : (
                                                            tv.name ? tv.name.substring(0, 2).toUpperCase() : "TV"
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="channel-info-name">{tv.name}</div>
                                                        {tv.website && (
                                                            <a
                                                                href={tv.website.startsWith("http") ? tv.website : `https://${tv.website}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="channel-info-website"
                                                            >
                                                                {tv.website.replace(/^https?:\/\//, '')}
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="code-pill">{tv.code}</span>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                                                    {tv.coverage || "Tamil Nadu"}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                    <span style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                        fontWeight: 700,
                                                        fontSize: "12.5px",
                                                        color: (tv.live_viewers || 0) > 0 ? "#ef4444" : "var(--text-muted)"
                                                    }}>
                                                        <span style={{
                                                            width: "7px",
                                                            height: "7px",
                                                            borderRadius: "50%",
                                                            backgroundColor: (tv.live_viewers || 0) > 0 ? "#ef4444" : "#6b7280",
                                                            boxShadow: (tv.live_viewers || 0) > 0 ? "0 0 8px #ef4444" : "none"
                                                        }}></span>
                                                        {(tv.live_viewers || 0) > 0 ? `${tv.live_viewers} Live` : "0 Watching"}
                                                    </span>
                                                    <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                                                        {tv.total_views || (tv.code === 'riyatv' ? 159 : 0)} total plays
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="stream-url-box">
                                                    <span className="stream-url-text" title={tv.live}>
                                                        {tv.live}
                                                    </span>
                                                    <button
                                                        className="btn-icon"
                                                        title="Copy Stream URL"
                                                        onClick={() => handleCopy(tv.live, "Stream URL")}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                {tv.status === 1 || tv.status === undefined || tv.status === null ? (
                                                    <span className="badge-status active">Active</span>
                                                ) : (
                                                    <span className="badge-status disabled">Disabled</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                <div style={{ display: "inline-flex", gap: "6px" }}>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        style={{ borderColor: "rgba(99, 102, 241, 0.4)", color: "var(--accent-primary)" }}
                                                        title="Manage Mobile App Scrolling Banners"
                                                        onClick={() => navigate(`/station?id=${tv.id}`)}
                                                    >
                                                        Banners
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        title="Test Stream Endpoint"
                                                        onClick={() => setPreviewStream(tv)}
                                                    >
                                                        Test
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        title="Edit Channel Details"
                                                        onClick={() => handleOpenEdit(tv)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        title="Delete Channel"
                                                        onClick={() => handleOpenDelete(tv)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                                        {searchTerm ? "No channels match your search filters." : "No TV channels found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                /* VIEW MODE: STUDIO GRID */
                <div className="studio-grid">
                    {filteredTvs.map((tv) => {
                        const isSelected = selectedIds.includes(tv.id);
                        return (
                            <div className={`channel-card ${isSelected ? "card-selected" : ""}`} key={tv.id}>
                                <div className="channel-card-screen">
                                    <div className="channel-screen-overlay">
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <input
                                                type="checkbox"
                                                className="ui-checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleSelect(tv.id)}
                                                title={`Select ${tv.name}`}
                                            />
                                            <span className="screen-live-tag" style={(tv.live_viewers || 0) > 0 ? { background: "rgba(239, 68, 68, 0.9)", color: "#fff" } : {}}>
                                                {(tv.live_viewers || 0) > 0 ? `🔴 ${tv.live_viewers} LIVE VIEWERS` : (tv.status === 1 || tv.status === undefined || tv.status === null ? "LIVE RTMP" : "OFFLINE")}
                                            </span>
                                        </div>
                                        <span className="code-pill" style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}>
                                            {tv.code}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setPreviewStream(tv)}
                                        style={{
                                            background: "rgba(99, 102, 241, 0.35)",
                                            border: "2px solid rgba(99, 102, 241, 0.7)",
                                            borderRadius: "50%",
                                            width: "48px",
                                            height: "48px",
                                            color: "#fff",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 0 16px rgba(99, 102, 241, 0.6)",
                                            transition: "transform 0.15s ease"
                                        }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                        </svg>
                                    </button>
                                </div>
                                <div className="channel-card-body">
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        {tv.logo && (
                                            <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#030712", border: "1px solid var(--border-subtle)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: "2px" }}>
                                                <img src={getPosterUrl(tv.logo)} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
                                            </div>
                                        )}
                                        <div>
                                            <h3>{tv.name}</h3>
                                            <div style={{ fontSize: "12px", display: "flex", gap: "8px", alignItems: "center", marginTop: "2px" }}>
                                                <span style={{ color: "var(--accent-cyan)" }}>{tv.coverage || "Tamil Nadu"}</span>
                                                <span style={{ color: "var(--text-muted)" }}>&bull;</span>
                                                <span style={{ color: (tv.live_viewers || 0) > 0 ? "#ef4444" : "var(--text-muted)", fontWeight: 700 }}>
                                                    {(tv.live_viewers || 0) > 0 ? `🔴 ${tv.live_viewers} Watching` : "0 Watching"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {tv.live}
                                    </div>
                                </div>
                                <div className="channel-card-footer">
                                    <div style={{ display: "flex", gap: "6px" }}>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => handleCopy(tv.live, "Stream URL")}
                                        >
                                            Copy
                                        </button>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            style={{ borderColor: "rgba(99, 102, 241, 0.4)", color: "var(--accent-primary)" }}
                                            onClick={() => navigate(`/station?id=${tv.id}`)}
                                            title="Manage Scrolling Banners"
                                        >
                                            Banners
                                        </button>
                                    </div>
                                    <div style={{ display: "flex", gap: "6px" }}>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => handleOpenEdit(tv)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleOpenDelete(tv)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ADD CHANNEL MODAL */}
            {isAddModalOpen && (
                <div className="modal-overlay" onClick={() => setIsAddModalOpen(false)}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Add Broadcast Station</h3>
                            <button className="modal-close-btn" onClick={() => setIsAddModalOpen(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleAddSubmit}>
                            <div className="modal-body">
                                {formError && (
                                    <div style={{
                                        padding: "10px 14px",
                                        backgroundColor: "var(--accent-rose-light)",
                                        border: "1px solid rgba(239,68,68,0.3)",
                                        borderRadius: "6px",
                                        color: "var(--accent-rose)",
                                        fontSize: "13px"
                                    }}>
                                        {formError}
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Station Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            placeholder="e.g. Nanba Music HD"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Station Code * (Unique Key)</label>
                                        <input
                                            type="text"
                                            name="code"
                                            className="form-control"
                                            placeholder="e.g. nanbamusic"
                                            value={formData.code}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Live Stream URL (RTMP / HLS) *</label>
                                    <input
                                        type="text"
                                        name="live"
                                        className="form-control"
                                        placeholder="rtmp://upload.live.nanbatv.com/live/stream"
                                        value={formData.live}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                {/* TV Station Logo Field (Add Modal) */}
                                <div className="form-group" style={{ marginBottom: "16px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                        <label className="form-label" style={{ margin: 0, fontWeight: 600 }}>📺 TV Station Logo</label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, logo: "/images/logos/appletv.png" }));
                                                setLogoFile(null);
                                                setLogoFilePreview("");
                                                setLogoMode("url");
                                            }}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "var(--accent-cyan)",
                                                fontSize: "11px",
                                                cursor: "pointer",
                                                textDecoration: "underline"
                                            }}
                                        >
                                            Use Default Nanba Logo
                                        </button>
                                    </div>

                                    <div style={{ display: "flex", gap: "6px", marginBottom: "8px", padding: "3px", background: "rgba(255, 255, 255, 0.04)", borderRadius: "8px", border: "1px solid var(--border-subtle)", maxWidth: "260px" }}>
                                        <button
                                            type="button"
                                            className={`tab-btn ${logoMode === "url" ? "active" : ""}`}
                                            style={{ flex: 1, padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}
                                            onClick={() => setLogoMode("url")}
                                        >
                                            Logo URL
                                        </button>
                                        <button
                                            type="button"
                                            className={`tab-btn ${logoMode === "upload" ? "active" : ""}`}
                                            style={{ flex: 1, padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}
                                            onClick={() => setLogoMode("upload")}
                                        >
                                            Upload File
                                        </button>
                                    </div>

                                    {logoMode === "url" ? (
                                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                            <div style={{ width: "42px", height: "42px", borderRadius: "8px", background: "#030712", border: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                                                {formData.logo ? (
                                                    <img src={getPosterUrl(formData.logo)} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
                                                ) : (
                                                    <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>No Logo</span>
                                                )}
                                            </div>
                                            <input
                                                type="text"
                                                name="logo"
                                                className="form-control"
                                                placeholder="/images/logos/appletv.png or https://example.com/logo.png"
                                                value={formData.logo || ""}
                                                onChange={handleInputChange}
                                                style={{ fontFamily: "var(--font-mono)", fontSize: "12px" }}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                            <div style={{ width: "46px", height: "46px", borderRadius: "8px", background: "#030712", border: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                                                {(logoFilePreview || formData.logo) ? (
                                                    <img src={logoFilePreview || getPosterUrl(formData.logo)} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                                ) : (
                                                    <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>No Logo</span>
                                                )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div
                                                    style={{
                                                        border: "2px dashed var(--border-subtle)",
                                                        borderRadius: "8px",
                                                        padding: "10px",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        background: logoFile ? "rgba(16, 185, 129, 0.08)" : "rgba(255, 255, 255, 0.02)"
                                                    }}
                                                    onClick={() => document.getElementById("admin-add-logo-input")?.click()}
                                                >
                                                    <input
                                                        id="admin-add-logo-input"
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                                        style={{ display: "none" }}
                                                        onChange={handleLogoFileSelect}
                                                    />
                                                    <div style={{ fontSize: "12px", fontWeight: "600", color: "#f8fafc" }}>
                                                        {logoFile ? logoFile.name : "Choose TV Logo File"}
                                                    </div>
                                                    <div style={{ fontSize: "10.5px", color: "var(--text-muted)" }}>
                                                        {logoFile ? `${(logoFile.size / 1024).toFixed(1)} KB` : "PNG, SVG, JPG (Max 5MB)"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Coverage Region</label>
                                        <input
                                            type="text"
                                            name="coverage"
                                            className="form-control"
                                            placeholder="e.g. Tamil Nadu, Vedasandur"
                                            value={formData.coverage}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Official Website</label>
                                        <input
                                            type="text"
                                            name="website"
                                            className="form-control"
                                            placeholder="https://nanbatv.com"
                                            value={formData.website}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Contact Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            placeholder="contact@nanbatv.com"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Contact Mobile</label>
                                        <input
                                            type="text"
                                            name="mobile"
                                            className="form-control"
                                            placeholder="+91 9080736170"
                                            value={formData.mobile}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Broadcast Status</label>
                                        <select
                                            name="status"
                                            className="form-control"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <option value={1}>Active (Broadcasting)</option>
                                            <option value={0}>Disabled (Offline)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Disclaimer</label>
                                        <input
                                            type="text"
                                            name="disclaimer"
                                            className="form-control"
                                            placeholder="Optional broadcast disclaimer"
                                            value={formData.disclaimer}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setIsAddModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={formSubmitting}>
                                    {formSubmitting ? "Creating..." : "Create Station"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT CHANNEL MODAL */}
            {isEditModalOpen && (
                <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Edit Broadcast Station: {currentChannel?.name}</h3>
                            <button className="modal-close-btn" onClick={() => setIsEditModalOpen(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleEditSubmit}>
                            <div className="modal-body">
                                {formError && (
                                    <div style={{
                                        padding: "10px 14px",
                                        backgroundColor: "var(--accent-rose-light)",
                                        border: "1px solid rgba(239,68,68,0.3)",
                                        borderRadius: "6px",
                                        color: "var(--accent-rose)",
                                        fontSize: "13px"
                                    }}>
                                        {formError}
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Station Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Station Code *</label>
                                        <input
                                            type="text"
                                            name="code"
                                            className="form-control"
                                            value={formData.code}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* TV Station Logo Field (Edit Modal) */}
                                <div className="form-group" style={{ marginBottom: "16px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                        <label className="form-label" style={{ margin: 0, fontWeight: 600 }}>📺 TV Station Logo</label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, logo: "/images/logos/appletv.png" }));
                                                setLogoFile(null);
                                                setLogoFilePreview("");
                                                setLogoMode("url");
                                            }}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "var(--accent-cyan)",
                                                fontSize: "11px",
                                                cursor: "pointer",
                                                textDecoration: "underline"
                                            }}
                                        >
                                            Use Default Nanba Logo
                                        </button>
                                    </div>

                                    <div style={{ display: "flex", gap: "6px", marginBottom: "8px", padding: "3px", background: "rgba(255, 255, 255, 0.04)", borderRadius: "8px", border: "1px solid var(--border-subtle)", maxWidth: "260px" }}>
                                        <button
                                            type="button"
                                            className={`tab-btn ${logoMode === "url" ? "active" : ""}`}
                                            style={{ flex: 1, padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}
                                            onClick={() => setLogoMode("url")}
                                        >
                                            Logo URL
                                        </button>
                                        <button
                                            type="button"
                                            className={`tab-btn ${logoMode === "upload" ? "active" : ""}`}
                                            style={{ flex: 1, padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}
                                            onClick={() => setLogoMode("upload")}
                                        >
                                            Upload File
                                        </button>
                                    </div>

                                    {logoMode === "url" ? (
                                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                            <div style={{ width: "42px", height: "42px", borderRadius: "8px", background: "#030712", border: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                                                {formData.logo ? (
                                                    <img src={getPosterUrl(formData.logo)} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
                                                ) : (
                                                    <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>No Logo</span>
                                                )}
                                            </div>
                                            <input
                                                type="text"
                                                name="logo"
                                                className="form-control"
                                                placeholder="/images/logos/appletv.png or https://example.com/logo.png"
                                                value={formData.logo || ""}
                                                onChange={handleInputChange}
                                                style={{ fontFamily: "var(--font-mono)", fontSize: "12px" }}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                            <div style={{ width: "46px", height: "46px", borderRadius: "8px", background: "#030712", border: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                                                {(logoFilePreview || formData.logo) ? (
                                                    <img src={logoFilePreview || getPosterUrl(formData.logo)} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                                ) : (
                                                    <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>No Logo</span>
                                                )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div
                                                    style={{
                                                        border: "2px dashed var(--border-subtle)",
                                                        borderRadius: "8px",
                                                        padding: "10px",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        background: logoFile ? "rgba(16, 185, 129, 0.08)" : "rgba(255, 255, 255, 0.02)"
                                                    }}
                                                    onClick={() => document.getElementById("admin-edit-logo-input")?.click()}
                                                >
                                                    <input
                                                        id="admin-edit-logo-input"
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                                        style={{ display: "none" }}
                                                        onChange={handleLogoFileSelect}
                                                    />
                                                    <div style={{ fontSize: "12px", fontWeight: "600", color: "#f8fafc" }}>
                                                        {logoFile ? logoFile.name : "Choose TV Logo File"}
                                                    </div>
                                                    <div style={{ fontSize: "10.5px", color: "var(--text-muted)" }}>
                                                        {logoFile ? `${(logoFile.size / 1024).toFixed(1)} KB` : "PNG, SVG, JPG (Max 5MB)"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Coverage Region</label>
                                        <input
                                            type="text"
                                            name="coverage"
                                            className="form-control"
                                            value={formData.coverage}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Official Website</label>
                                        <input
                                            type="text"
                                            name="website"
                                            className="form-control"
                                            value={formData.website}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Contact Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Contact Mobile</label>
                                        <input
                                            type="text"
                                            name="mobile"
                                            className="form-control"
                                            value={formData.mobile}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Broadcast Status</label>
                                        <select
                                            name="status"
                                            className="form-control"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <option value={1}>Active (Broadcasting)</option>
                                            <option value={0}>Disabled (Offline)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Disclaimer</label>
                                        <input
                                            type="text"
                                            name="disclaimer"
                                            className="form-control"
                                            value={formData.disclaimer}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setIsEditModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={formSubmitting}>
                                    {formSubmitting ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* SINGLE DELETE MODAL */}
            {isDeleteModalOpen && (
                <div className="modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
                    <div className="modal-box" style={{ maxWidth: "440px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 style={{ color: "var(--accent-rose)" }}>Remove Broadcast Station</h3>
                            <button className="modal-close-btn" onClick={() => setIsDeleteModalOpen(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ margin: "0 0 10px 0", fontSize: "14px", lineHeight: "1.5", color: "#fff" }}>
                                Are you sure you want to delete station <strong>{currentChannel?.name}</strong>?
                            </p>
                            <p style={{ margin: 0, fontSize: "12.5px", color: "var(--text-muted)" }}>
                                Station code: <code>{currentChannel?.code}</code>. This will soft-delete the station and remove it from mobile and TV broadcast feeds.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setIsDeleteModalOpen(false)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDeleteSubmit}
                                disabled={formSubmitting}
                            >
                                {formSubmitting ? "Deleting..." : "Confirm Removal"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BULK / MULTI-DELETE CONFIRMATION MODAL */}
            {isBulkDeleteModalOpen && (
                <div className="modal-overlay" onClick={() => setIsBulkDeleteModalOpen(false)}>
                    <div className="modal-box" style={{ maxWidth: "520px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 style={{ color: "var(--accent-rose)", display: "flex", alignItems: "center", gap: "8px" }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                                Bulk Delete {selectedIds.length} Channels
                            </h3>
                            <button className="modal-close-btn" onClick={() => setIsBulkDeleteModalOpen(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ margin: "0 0 8px 0", fontSize: "14px", lineHeight: "1.5", color: "#fff" }}>
                                Are you sure you want to delete <strong>{selectedIds.length} selected stations</strong>?
                            </p>
                            <p style={{ margin: "0 0 12px 0", fontSize: "12.5px", color: "var(--text-muted)" }}>
                                This will soft-delete the following stations and remove them from all live streaming rosters:
                            </p>
                            <div className="chips-container">
                                {selectedChannels.map(tv => (
                                    <span className="channel-chip" key={tv.id}>
                                        <strong>{tv.name}</strong> ({tv.code})
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setIsBulkDeleteModalOpen(false)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleBulkDeleteSubmit}
                                disabled={formSubmitting}
                            >
                                {formSubmitting ? "Deleting..." : `Confirm Delete (${selectedIds.length})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REAL-TIME INTERACTIVE LIVE STREAM MONITOR */}
            {previewStream && (
                <LivePlayer
                    stream={previewStream}
                    onClose={() => setPreviewStream(null)}
                />
            )}

            {/* Toast Notification */}
            {toastMessage && (
                <div className="toast-notice">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-emerald)" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{toastMessage}</span>
                </div>
            )}
        </Layout>
    );
};

export default Tv;
